import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { discoverExistingCharts } from "../../excel/charts/api/registry";
import type { ChartCardProps } from "../../excel/charts/api";
import { useProject } from "../context/ProjectContext";
import { useWorksheetListeners } from "../context/WorksheetListenerContext";
import { handleError } from "../../errors";
import Logger from "../../clients/sentry";

/**
 * Charts already in the workbook, rediscovered whenever it might have changed: on estimate load, on
 * sheet delete/rename, and after a chart is created or removed.
 *
 * Only the first load for an estimate reports itself as loading; a rediscovery that replaces cards
 * already on screen stays silent.
 *
 * The charts carry the options `discoverExistingCharts` already resolved, so cards never refetch them.
 */
export function useChartCards() {
  const { selectedEstimate } = useProject();
  const { registerListeners } = useWorksheetListeners();

  const [charts, setCharts] = useState<ChartCardProps[]>([]);
  // Seeded from whether there is anything to discover, so the first paint is already right.
  const [loadingCharts, setLoadingCharts] = useState(() => !!selectedEstimate);
  const isLoadingRef = useRef(false);
  const debouncedReloadRef = useRef<ReturnType<typeof debounce> | undefined>(undefined);
  // reloadCharts is memoized with no deps so the handler handed to Excel stays stable, so it reads
  // the current estimate through a ref rather than a closed-over value.
  const selectedEstimateRef = useRef(selectedEstimate);
  selectedEstimateRef.current = selectedEstimate;

  const reloadCharts = useCallback(async (options?: { showLoading?: boolean }) => {
    // The sheet-event handler calls this regardless of what is loaded.
    if (!selectedEstimateRef.current) {
      setCharts([]);
      // Unconditional: this is what releases the loading state the hook seeds itself with.
      setLoadingCharts(false);
      return;
    }

    isLoadingRef.current = true;
    if (options?.showLoading) setLoadingCharts(true);
    try {
      const [existingCharts] = await discoverExistingCharts();
      // Re-checked: the project header sits on this page, so a clear can land while discovery is in
      // flight. Writing then would paint the old cards back over the empty state.
      if (!selectedEstimateRef.current) return;
      setCharts(existingCharts);
    } catch (error) {
      const err = handleError(error, { context: "discoverExistingCharts" });
      Logger.error("Failed to discover charts", err);
      throw err;
    } finally {
      isLoadingRef.current = false;
      if (options?.showLoading) setLoadingCharts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedEstimate) {
      setCharts([]);
      setLoadingCharts(false);
      return;
    }

    const initialize = async () => {
      // The one path with nothing on screen yet, so the only one that shows skeletons.
      await reloadCharts({ showLoading: true });
      await registerListeners(() => debouncedReloadRef.current?.());
    };

    void initialize().catch(() => {
      // Logged in reloadCharts; the page surfaces the empty state.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstimate]);

  useEffect(() => {
    // Indirected through a ref so the handler handed to Excel stays stable across reloads.
    debouncedReloadRef.current = debounce(() => {
      if (!isLoadingRef.current) void reloadCharts().catch(() => {});
    }, 100);

    return () => {
      debouncedReloadRef.current?.cancel?.();
    };
  }, [reloadCharts]);

  return { charts, loadingCharts, reloadCharts };
}

export default useChartCards;
